// Wraps every V2 page with a single sub-nav (合同 v2 · Demo title +
// nav links + viewer toggle). The global Layout/Header above renders
// once for the whole app, so we deliberately don't add another <header>
// here — the sub-nav sits below the global one as a sticky strip.
//
// The viewer toggle isn't a product feature; it's a demo affordance.

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useContractsV2 } from './ContractsV2Context';
import { seedBundles } from './mockData';

export const ContractsV2Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { viewerId, dispatch, bundles } = useContractsV2();
  const loc = useLocation();
  const someBundle = bundles[0] || seedBundles[0];
  const client = someBundle.contract.client;
  const creator = someBundle.contract.creator;

  return (
    // Global Layout/Header is `fixed top-0 z-50` and overlays the page hero
    // by design. We need to push our demo content below it; pt-20 ≈ the
    // collapsed header height. The sub-nav itself is sticky so the viewer
    // toggle stays accessible while scrolling the project page.
    <div className="bg-slate-50 min-h-screen pt-20">
      <div className="border-b border-slate-200 bg-white sticky top-20 z-40">
        <div className="max-w-6xl mx-auto px-6 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-5">
            <Link to="/contracts-v2" className="text-sm font-semibold text-slate-900">
              合同 v2 · Demo
            </Link>
            <nav className="flex items-center gap-2 text-sm">
              <Link
                to="/contracts-v2"
                className={`px-2.5 py-1 rounded-md transition-colors ${
                  loc.pathname === '/contracts-v2'
                    ? 'bg-slate-100 text-slate-900 font-medium'
                    : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                我的合同
              </Link>
              <Link
                to="/contracts-v2/new"
                className={`px-2.5 py-1 rounded-md transition-colors ${
                  loc.pathname === '/contracts-v2/new'
                    ? 'bg-slate-100 text-slate-900 font-medium'
                    : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                发起新合同
              </Link>
            </nav>
          </div>

          <div className="flex items-center gap-2 text-xs">
            <span className="text-slate-400">视角：</span>
            <button
              type="button"
              onClick={() => dispatch({ type: 'SET_VIEWER', viewerId: client.userId })}
              className={`px-2.5 py-1 rounded-md transition-colors ${
                viewerId === client.userId
                  ? 'bg-blue-50 border border-blue-200 text-blue-700 font-medium'
                  : 'border border-slate-200 text-slate-500 hover:bg-slate-50'
              }`}
            >
              客户：{client.nickname}
            </button>
            <button
              type="button"
              onClick={() => dispatch({ type: 'SET_VIEWER', viewerId: creator.userId })}
              className={`px-2.5 py-1 rounded-md transition-colors ${
                viewerId === creator.userId
                  ? 'bg-emerald-50 border border-emerald-200 text-emerald-700 font-medium'
                  : 'border border-slate-200 text-slate-500 hover:bg-slate-50'
              }`}
            >
              创作者：{creator.nickname}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">{children}</div>
    </div>
  );
};
